import { useState, useMemo } from 'react';
import { RankingTable, LoadingSpinner } from './ui';
import { FilterControls } from './ui/FilterControls';
import { Pagination } from './ui/Pagination';

export default function FilterableTable({
  data = [],
  columns,
  defaultItemsPerPage = 10,
  searchFields = [],
  filters = [],
  className = '',
  showPagination = true,
  showFilters = true,
  searchPlaceholder = 'Search...',
  cellPadding = 'compact',
  sortable = true,
  defaultSortColumn = null,
  defaultSortDirection = 'desc',
  loading = false
}) {
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
  const [filterValues, setFilterValues] = useState(
    filters.reduce((acc, filter) => ({ ...acc, [filter.key]: '' }), {})
  );
  const [sortColumn, setSortColumn] = useState(defaultSortColumn);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);

  // Apply search filter
  const searchedData = useMemo(() => {
    if (!searchValue || searchFields.length === 0) return data;

    return data.filter(item =>
      searchFields.some(field => {
        const value = field.split('.').reduce((obj, key) => obj?.[key], item);
        return value?.toString().toLowerCase().includes(searchValue.toLowerCase());
      })
    );
  }, [data, searchValue, searchFields]);

  // Apply column filters
  const filteredData = useMemo(() => {
    return searchedData.filter(item => {
      return filters.every(filter => {
        const filterValue = filterValues[filter.key];
        if (!filterValue) return true;

        const itemValue = filter.key.split('.').reduce((obj, key) => obj?.[key], item);
        return itemValue === filterValue;
      });
    });
  }, [searchedData, filterValues, filters]);

  // Apply sort to the full filtered dataset (before pagination)
  const sortedData = useMemo(() => {
    if (sortColumn === null || sortDirection === null) return filteredData;
    const col = columns[sortColumn];
    if (!col) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = col.sortValue ? col.sortValue(a) : a[col.key];
      const bVal = col.sortValue ? col.sortValue(b) : b[col.key];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [filteredData, sortColumn, sortDirection, columns]);

  // Calculate pagination from sorted+filtered data
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  const handleSearchChange = (value) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilterValues(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleSort = (columnIndex, direction) => {
    setSortColumn(columnIndex);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  // Prepare filter controls
  const filterControls = filters.map(filter => ({
    label: filter.label,
    placeholder: filter.placeholder,
    value: filterValues[filter.key],
    onChange: (value) => handleFilterChange(filter.key, value),
    options: filter.options
  }));

  // Convert columns format to RankingTable format
  const rankingHeaders = columns.map(col => ({
    label: col.header,
    align: col.align || 'left'
  }));

  const rankingColumns = columns.map(col => ({
    key: col.key,
    align: col.align || 'left',
    className: col.className || '',
    render: col.render,
    sortValue: col.sortValue
  }));

  return (
    <div className={className}>
      {showFilters && (
        <FilterControls
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          searchPlaceholder={searchPlaceholder}
          filters={filterControls}
        />
      )}

      <RankingTable
        headers={rankingHeaders}
        data={paginatedData}
        columns={rankingColumns}
        cellPadding={cellPadding}
        sortable={sortable}
        defaultSortColumn={defaultSortColumn}
        defaultSortDirection={defaultSortDirection}
        onSort={handleSort}
      />

      {filteredData.length === 0 && (
        <div className="text-center py-8">
          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="text-text-muted-light dark:text-text-muted">
              No results found
            </div>
          )}
        </div>
      )}

      {showPagination && totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}
    </div>
  );
}
